import React from "react";
var __jsx = React.createElement;
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightArrowLeft, faArrowsRotate, faInbox, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { useSyncStatus } from "@/hooks/outbound";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { StateView } from "@/components/state-view";
import { Button } from "@/components/button";
import { Modal } from "@/components/modal";
import { SyncSubmissionsTable } from "./sync-submissions-table";
/** Toolbar button opening a read-only modal of outbound sync submission statuses. */
export function BidirectionalSync(_ref) {
  var _sync$data$pages$at$t, _sync$data2;
  var workspaceId = _ref.workspaceId,
    patientId = _ref.patientId;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(false),
    isOpen = _useState[0],
    setIsOpen = _useState[1];
  var sync = useSyncStatus(workspaceId, patientId, isOpen);
  var rows = useMemo(function () {
    var _sync$data$pages$flat, _sync$data;
    return (_sync$data$pages$flat = (_sync$data = sync.data) === null || _sync$data === void 0 ? void 0 : _sync$data.pages.flatMap(function (page) {
      return page.requests;
    })) !== null && _sync$data$pages$flat !== void 0 ? _sync$data$pages$flat : [];
  }, [sync.data]);
  var totalCount = (_sync$data$pages$at$t = (_sync$data2 = sync.data) === null || _sync$data2 === void 0 || (_sync$data2 = _sync$data2.pages.at(-1)) === null || _sync$data2 === void 0 ? void 0 : _sync$data2.total_count) !== null && _sync$data$pages$at$t !== void 0 ? _sync$data$pages$at$t : rows.length;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    variant: "secondary",
    leftIcon: faArrowRightArrowLeft,
    "data-testid": "bidirectional-sync-open",
    title: t("bidirectional_sync", {
      defaultValue: "Bidirectional sync"
    }),
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.BIDIRECTIONAL_SYNC_OPEN, {
        patientId: patientId
      });
      setIsOpen(true);
    }
  }), /*#__PURE__*/React.createElement(Modal, {
    opened: isOpen,
    onClose: function onClose() {
      return setIsOpen(false);
    },
    size: 1280,
    title: t("sync_results", {
      defaultValue: "Sync results"
    }),
    subtitle: t("sync_results_subtitle", {
      defaultValue: "Outbound submissions sent to the EMR for this patient."
    }),
    closeTestId: "bidirectional-sync-close",
    footer: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "mr-auto text-style-label-base text-sds-base-tertiary"
    }, t("submission_count", {
      loaded: rows.length,
      total: totalCount,
      defaultValue: "{{loaded}} of {{total}} submissions"
    })), /*#__PURE__*/React.createElement(Button, {
      variant: "secondary",
      "data-testid": "bidirectional-sync-refresh",
      disabled: sync.isFetching,
      onClick: function onClick() {
        trackEvent(ANALYTICS_EVENTS.BIDIRECTIONAL_SYNC_REFRESH, {
          patientId: patientId
        });
        sync.refetch();
      }
    }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
      icon: faArrowsRotate,
      className: "h-3.5 w-3.5 ".concat(sync.isFetching ? "animate-spin" : "")
    }), t("refresh", {
      defaultValue: "Refresh"
    })))
  }, sync.isLoading ? /*#__PURE__*/React.createElement("p", {
    className: "p-6 text-style-body-small text-sds-base-tertiary"
  }, t("loading")) : sync.isError ? /*#__PURE__*/React.createElement(StateView, {
    intent: "urgent",
    icon: faTriangleExclamation,
    title: t("failed_to_load", {
      defaultValue: "Couldn't load sync results"
    }),
    onRetry: function onRetry() {
      return sync.refetch();
    }
  }) : rows.length ? /*#__PURE__*/React.createElement(SyncSubmissionsTable, {
    rows: rows,
    hasMore: Boolean(sync.hasNextPage),
    isLoadingMore: sync.isFetchingNextPage,
    onLoadMore: sync.fetchNextPage
  }) : /*#__PURE__*/React.createElement(StateView, {
    icon: faInbox,
    title: t("no_sync_submissions", {
      defaultValue: "No sync submissions"
    }),
    description: t("no_sync_submissions_desc", {
      defaultValue: "Nothing has been submitted to the EMR for this patient yet."
    })
  })));
}